import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, pipe, switchMap, take, tap, finalize, EMPTY, catchError, map, firstValueFrom, from } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { AuthService } from '@auth0/auth0-angular';
import { ProfileDto } from '../dtos/user-profile.dto';
import { UserService } from '../services/user.service';
import { Auth0UserProfile } from '../components/models/user.mode';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BalancesStore } from './balances.store';
import { ReportsStore } from './reports.store';
import { ToastrFactoryService } from '../services/toastr-factory.service';
import { environment, systemLanguages } from 'src/environments/environment';
import { LoadingService } from '../services/loading.service';
import { ModalsService } from '../services/modals.service';
import { TermsModalComponent } from 'src/app/components/modals/terms-modal/terms-modal.component';
import { UTMDto } from '../dtos/utm.dto';
import countries from 'src/assets/config/country-list';
import { DomainOwnerModalComponent } from '../components/modals/domain-owner-modal/domain-owner-modal.component';
import { isCommonDomain } from '../config/domains.config';
import { TourService } from '../services/tour/tour.service';
import { convertImageUrlToFile } from '../utils/image-utils';
import { getValidLanguage } from '../utils/get-valid-language-util';
import { ValidateEmailModalComponent } from '../components/modals/validate-email-modal/validate-email-modal.component';
import { OptionalSsoInfoModalComponent } from '../components/modals/optional-sso-info-modal/optional-sso-info-modal.component';
import GetSanitizedImageUrlUtil from '../utils/get-sanitized-image-url-util';

export interface Domain {
  name?: string;
  dnsName?: string;
  validationKey?: string;
  isValid: boolean;
  isOwnedByOtherUser: boolean;
  lastValidDate?: Date;
  lastCheckDate?: Date;
  nextCheckDate?: Date;
  ownerEmail?: string;
  description?: string;
  status?: string;
}

type AuthStoreState = {
  user: Auth0UserProfile | null;
  authenticated: boolean;
  profile: ProfileDto | null;
  domains: Domain[];
  profilePicture: SafeUrl | null;
  alreadyChecked: boolean;
};

const initialState: AuthStoreState = {
  user: null,
  authenticated: false,
  profile: null,
  domains: [],
  profilePicture: null,
  alreadyChecked: false,
};

export const AuthStore = signalStore(
  {
    providedIn: 'root',
  },
  withState(initialState),
  withComputed(({ user, profile, domains }) => {
    const userHasProfile = computed(() => {
      return profile() !== null;
    });

    const isGoogleAuth = computed(() => {
      return user()?.googleAuth;
    });

    const hasCommonDomain = computed(() => {
      return isCommonDomain(profile()?.email?.split('@')[1] || '');
    });

    const hasAvailableDomains = computed(() => {
      return domains().length > 0;
    });

    const isAccessRestrictedBilling = computed(() => {
      return profile()?.isAccessRestrictedBilling;
    });

    return {
      userHasProfile,
      isGoogleAuth,
      hasCommonDomain,
      hasAvailableDomains,
      isAccessRestrictedBilling,
    };
  }),
  withComputed(({ authenticated, alreadyChecked }) => {
    const authenticationChecked = computed(() => authenticated);
    const isAlreadyChecked = computed(() => alreadyChecked);

    return {
      authenticationChecked,
      isAlreadyChecked,
    };
  }),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      userService = inject(UserService),
      sanitizer = inject(DomSanitizer),
      translate = inject(TranslateService),
      router = inject(Router),
      balancesStore = inject(BalancesStore),
      reportsStore = inject(ReportsStore),
      toastrFactory = inject(ToastrFactoryService),
      loadingService = inject(LoadingService),
      modalsService = inject(ModalsService),
      tourService = inject(TourService),
      getSanitizedImageUrlUtil = inject(GetSanitizedImageUrlUtil),
    ) => {
      async function asyncCurrentUser(): Promise<void> {
        try {
          const isAuthenticated = await firstValueFrom(authService.isAuthenticated$);

          if (!isAuthenticated) {
            patchState(store, {
              authenticated: false,
              alreadyChecked: true,
            });
            return;
          }

          const user = await firstValueFrom(authService.user$.pipe(take(1), filter(Boolean)));

          if (!user?.email_verified) {
            await modalsService.openModal(ValidateEmailModalComponent);
            logout();
            return;
          }

          patchState(store, {
            user: user as Auth0UserProfile,
          });

          const profile = await firstValueFrom(userService.getUser());

          patchState(store, {
            profile,
            authenticated: true,
            alreadyChecked: true,
          });

          try {
            getProfilePicture();
          } catch (e) {
            console.error(e);
          }

          const userLanguage =
            profile.language ||
            (() => {
              const browserLang = navigator.language.toLowerCase();
              return systemLanguages.some((lang) => browserLang.startsWith(lang.language.toLowerCase()))
                ? systemLanguages.find((lang) => browserLang.startsWith(lang.language.toLowerCase()))!.language
                : 'en';
            })();

          if (!profile.language) {
            updateProfileLanguage({ language: userLanguage });
          }

          translate.use(getValidLanguage(userLanguage));
          checkPrivacyPolicyStatus(profile);

          balancesStore.loadBalances();
          reportsStore.loadReports();

          const domains = await firstValueFrom(userService.getDomain());

          patchState(store, {
            domains,
          });
        } catch (error) {
          console.error('Error during user initialization:', error);

          patchState(store, {
            authenticated: false,
            alreadyChecked: true,
            domains: [],
          });
        }
      }

      // Lets keep it for now
      const currentUser = rxMethod<void>(
        pipe(
          switchMap((_) =>
            authService.isAuthenticated$.pipe(
              tap((isAuthenticated) => {
                if (isAuthenticated === false) {
                  patchState(store, {
                    authenticated: false,
                    alreadyChecked: true,
                  });
                }
              }),
              filter(Boolean),
              switchMap(() => authService.user$.pipe(take(1), filter(Boolean))),
              tapResponse({
                next: async (user) => {
                  if (!user?.email_verified) {
                    const res = await modalsService.openModal(ValidateEmailModalComponent);
                    logout();
                  }
                  patchState(store, {
                    user: user as Auth0UserProfile,
                  });
                },
                error: (error) => {
                  console.error(error);
                },
              }),
            ),
          ),
          switchMap((_) =>
            userService.getUser().pipe(
              tapResponse({
                next: async (profile) => {
                  // profile.isAccessRestrictedBilling = true;
                  patchState(store, {
                    profile: profile,
                  });

                  patchState(store, {
                    authenticated: true,
                    alreadyChecked: true,
                  });

                  try {
                    getProfilePicture();
                  } catch (e) {
                    console.error(e);
                  }

                  const userLanguage =
                    profile.language ||
                    (() => {
                      const browserLang = navigator.language.toLowerCase();
                      return systemLanguages.some((lang) => browserLang.startsWith(lang.language.toLowerCase()))
                        ? systemLanguages.find((lang) => browserLang.startsWith(lang.language.toLowerCase()))!.language
                        : 'en';
                    })();

                  if (!profile.language) {
                    updateProfileLanguage({ language: userLanguage });
                  }

                  translate.use(getValidLanguage(userLanguage));

                  checkPrivacyPolicyStatus(profile);

                  // Initializes the balances and reports
                  // Only after the user and domains is loaded
                  balancesStore.loadBalances();
                  reportsStore.loadReports();
                },
                error: (error) => {
                  console.error('Error loading user profile:', error);
                  patchState(store, {
                    authenticated: false,
                    alreadyChecked: true,
                  });
                },
              }),
            ),
          ),
          switchMap((_) =>
            userService.getDomain().pipe(
              tapResponse({
                next: (domains) => {
                  patchState(store, {
                    domains: domains,
                  });
                },
                error: (error) => {
                  console.error('Error loading domains:', error);
                  patchState(store, {
                    domains: [],
                  });
                },
              }),
            ),
          ),
        ),
      );

      const isUserSSO = async () => {
        const newUser = store.user();
        if (!newUser) return;

        // Only call if reservedShortid exists in localStorage and this is initial login
        const reservedShortid = localStorage.getItem('reservedShortid');
        if (reservedShortid) {
          try {
            await userService.claimShortId(reservedShortid);
            toastrFactory.success(translate.instant('LOGIN.shortIdPreregistered'));
            // Remove from localStorage to ensure it's only processed once
            localStorage.removeItem('reservedShortid');
          } catch (error) {
            toastrFactory.error(translate.instant('LOGIN.shortIdPreregisteredFailed'));
          }
        }

        const utmInfo = getUTMInfo();
        const referrer = getReferrer();
        const userCountry = getUserCountry(newUser);

        const optionalSSOInfo = await modalsService.openModal(OptionalSsoInfoModalComponent);

        const userInfo: ProfileDto = {
          agreedPrivacyPolicy: true,
          name: newUser.name,
          publicName: newUser.name,
          websiteUrl: undefined,
          country: userCountry,
          agreedMarketingContact: optionalSSOInfo.agreedMarketingContact,
          signupReference: optionalSSOInfo.signupReference,
          umtCampaign: utmInfo.utmCampaign,
          umtMedium: utmInfo.utmMedium,
          umtSource: utmInfo.utmSource,
          referrer: referrer,
        };

        if (store.user()?.picture) {
          convertImageUrlToFile(store.user()?.picture, 'profilePicture.png')
            .then((imageFile) => {
              if (imageFile) {
                updateProfilePicture(imageFile);
              }
            })
            .catch(console.error);
        }

        patchState(store, {
          profile: {
            ...store.profile(),
            ...userInfo,
          },
        });

        if (userInfo.language) {
          // Set the language in the translation service
          translate.use(getValidLanguage(userInfo.language));
        }

        await firstValueFrom(setGeneralInfoForSocialSignup(userInfo, newUser));
      };

      function setUUIDRefer(uuidRefer: string | null | undefined) {
        if (!uuidRefer) return EMPTY;
        return userService.setUUIDRefer(uuidRefer).pipe(
          tapResponse({
            next: (response: any) => {
              localStorage.removeItem('uuidRefer');
              console.log('UUID refer set successfully:', response);
            },
            error: (error) => {
              console.error('Error setting refer:', error);
            },
          }),
          catchError(() => EMPTY),
        );
      }

      function getUserCountry(newUser: Auth0UserProfile): string {
        // Default to 'US' if no country is found
        const defaultCountry = 'US';
        const userLocale = newUser['https://example.com/country'] || '';

        // Return default immediately if no userLocale
        if (!userLocale) return defaultCountry;
        const c = countries.find((country) => country.name === userLocale)?.code;

        return c || defaultCountry;
      }

      function getUTMInfo(): UTMDto {
        const utmSettingsItem = localStorage.getItem('utmSettings');
        if (utmSettingsItem) {
          try {
            return JSON.parse(utmSettingsItem);
          } catch (error) {
            console.error('Error parsing JSON from localStorage:', error);
          }
        }
        return {};
      }

      function getReferrer(): string | undefined {
        return localStorage.getItem('referrer') || undefined;
      }

      function setGeneralInfoForSocialSignup(userInfo: ProfileDto, newUser: Auth0UserProfile) {
        return userService.editGeneralUserInfo(userInfo).pipe(
          tapResponse({
            next: (profile: ProfileDto) => {
              // Update the store with the new profile
              patchState(store, {
                profile: profile,
              });

              const uuidRefer = localStorage.getItem('uuidRefer');

              setUUIDRefer(uuidRefer);

              // Set the language in the translation service
              translate.use(getValidLanguage(profile.language));

              startWelcomeTour();
            },
            error: (error) => {
              console.error('Error setting general info:', error);
            },
          }),
          catchError(() => EMPTY),
        );
      }

      function startWelcomeTour() {
        router.navigate(['/settings']).then(() => {
          tourService.startTour('SETTINGS.OVERVIEW');
        });
      }

      function normalizeWebsiteUrl(profile: Partial<ProfileDto>): void {
        if (!profile.websiteUrl || profile.websiteUrl.trim() === '') {
          profile.websiteUrl = 'https://';
          return;
        }
        if (!profile.websiteUrl.match(/^https?:\/\//)) {
          profile.websiteUrl = 'https://' + profile.websiteUrl;
        }
      }

      const updateProfileInfo = rxMethod<Partial<ProfileDto>>(
        pipe(
          map((profileUpdate) => {
            normalizeWebsiteUrl(profileUpdate);
            return profileUpdate;
          }),
          switchMap((profileUpdate) =>
            userService.editGeneralUserInfo(profileUpdate).pipe(
              tapResponse({
                next: (profile: ProfileDto) => {
                  patchState(store, {
                    profile: profile,
                  });

                  if (!profile.name && !profile.publicName && store.user()?.googleAuth) {
                    isUserSSO();
                    return;
                  }

                  toastrFactory.success(translate.instant('PROFILE.userInfoUpdatedSuccessfully'), '', {
                    timeOut: 3000,
                  });
                },
                error: (error) => {
                  toastrFactory.error(translate.instant('unknownError'), '', {
                    timeOut: 3000,
                  });
                  console.error('Error updating profile:', error);
                },
              }),
            ),
          ),
        ),
      );

      const updateCommunicationSettings = rxMethod<Partial<ProfileDto>>(
        pipe(
          switchMap((profileUpdate) =>
            userService.editGeneralUserInfo(profileUpdate).pipe(
              tapResponse({
                next: (profile: ProfileDto) => {
                  patchState(store, {
                    profile: profile,
                  });

                  toastrFactory.success(translate.instant('communicationPreferencesSavedSuccessfully'), '', {
                    timeOut: 3000,
                  });
                },
                error: (error) => {
                  toastrFactory.error(translate.instant('unknownError'), '', {
                    timeOut: 3000,
                  });
                  console.error('Error updating profile:', error);
                },
              }),
            ),
          ),
        ),
      );

      const handleFirstTour = () => {
        const user = store.user();
        const hasEnabledTour = user?.showFirstLoginTour;
        const hasSkippedTour = localStorage.getItem('skipTour');

        if (hasEnabledTour && !hasSkippedTour && !user?.testUser) {
          startWelcomeTour();
          localStorage.setItem('skipTour', 'true');
          return;
        }

        localStorage.removeItem('skipTour');
      };

      const checkPrivacyPolicyStatus = async (profile: any) => {
        if (profile.agreedPrivacyPolicy) {
          handleFirstTour();
          return;
        }

        console.log(getValidLanguage(profile.language), profile.language);
        const res = await modalsService.openModal(TermsModalComponent, { data: { profile } });

        if (res) {
          const domain = profile.email.split('@')[1];
          if (isCommonDomain(domain)) {
            handleAcceptedTerms();
          } else {
            const res = await modalsService.openModal(DomainOwnerModalComponent, { data: { domain } });
            if (res) {
              userService.addDomain(domain).subscribe({
                next: () => {
                  updateDomain(false);
                  handleAcceptedTerms();
                },
                error: (e: any) => {
                  toastrFactory.error(translate.instant('somethingWentWrongWhileWeAreTryingToAddYourDomain'));
                  console.error('Error updating privacy policy status:', e);
                },
              });
            } else {
              handleAcceptedTerms();
            }
          }
        } else {
          logout();
        }
      };

      const handleAcceptedTerms = rxMethod<void>(
        pipe(
          switchMap((_) =>
            userService.editGeneralUserInfo({ agreedPrivacyPolicy: true }).pipe(
              tapResponse({
                next: () => {
                  updateProfileInfo({ agreedPrivacyPolicy: true });
                },
                error: (error) => {
                  console.error('Error updating privacy policy status:', error);
                },
              }),
            ),
          ),
        ),
      );

      const updateProfileLanguage = rxMethod<Partial<ProfileDto>>(
        pipe(
          switchMap((profileUpdate) =>
            userService.setUserPreferedLanguage(profileUpdate.language!).pipe(
              tapResponse({
                next: (profile: ProfileDto) => {
                  patchState(store, {
                    profile: profile,
                  });
                  translate.use(getValidLanguage(profile.language));
                },
                error: (error) => {
                  console.error('Error updating profile:', error);
                },
              }),
            ),
          ),
        ),
      );

      const updateProfilePicture = rxMethod<File>(
        pipe(
          switchMap((userImg) => {
            const profile = store.profile();
            return userService.updateProfilePicture(userImg).pipe(
              switchMap(() =>
                from(getSanitizedImageUrlUtil.getSanizedImageUrlByUserId(profile?.id || '')).pipe(
                  tap((safeUrl) => {
                    patchState(store, { profilePicture: safeUrl });
                  }),
                ),
              ),
              tapResponse({
                next: () => {
                  toastrFactory.success(translate.instant('PROFILE.pictureUpdatedSuccessfully'), '', { timeOut: 3000 });
                },
                error: (error) => {
                  console.error('Error updating profile picture:', error);
                },
              }),
            );
          }),
        ),
      );

      const updateDomain = (shouldShowToastr: boolean = true) => {
        userService.getDomain().pipe(
          tapResponse({
            next: (domains) => {
              patchState(store, { domains });

              domains[0].isValid && shouldShowToastr
                ? toastrFactory.success(translate.instant('PROFILE.validatedDomain'), '', { timeOut: 3000 })
                : toastrFactory.warning(translate.instant('PROFILE.yourDnsStatusStillTheSame'), '', { timeOut: 3000 });
            },
            error: (error) => {
              toastrFactory.error(translate.instant('PROFILE.nickyWasUnableToValidateYourDomain'), '', {
                timeOut: 5000,
              });
              console.log(error);
            },
          }),
        );
      };
      const getProfilePicture = rxMethod<void>(
        pipe(
          switchMap(() =>
            userService.getProfilePicture().pipe(
              tapResponse({
                next: (response) => {
                  const safeUrl = sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(response as Blob));
                  patchState(store, {
                    profilePicture: safeUrl,
                  });
                },
                error: (error) => {
                  console.error('Error getting profile picture:', error);
                },
              }),
            ),
          ),
        ),
      );

      const logout = rxMethod<void>(
        pipe(
          tap(() => loadingService.setLoading(true)),
          tap(() => {
            // Clear all storage
            localStorage.clear();
            sessionStorage.clear();
            // Clear IndexedDB if used by your PWA
            indexedDB.databases().then((dbs) => {
              dbs.forEach((db) => {
                indexedDB.deleteDatabase(db.name!);
              });
            });
            // Clear cache if needed
            if ('caches' in window) {
              caches.keys().then((keys) => {
                keys.forEach((key) => caches.delete(key));
              });
            }
          }),
          switchMap(() => authService.logout({ logoutParams: { returnTo: environment.websiteUrl } })),
          tap(() => router.navigate(['/'])),
          finalize(() => loadingService.setLoading(false)),
        ),
      );

      return {
        logout,
        getProfilePicture,
        updateDomain,
        updateCommunicationSettings,
        updateProfilePicture,
        currentUser,
        asyncCurrentUser,
        updateProfileInfo,
        updateProfileLanguage,
      };
    },
  ),
);
